import os from 'os'

export interface ResourceThresholds {
  /** CPU 使用率阈值（百分比），默认 90 */
  cpuPercent?: number
  /** 内存使用率阈值（百分比），默认 90 */
  memoryPercent?: number
  /** 磁盘使用率阈值（百分比），默认 90 */
  diskPercent?: number
}

export interface ResourceAlert {
  type: 'cpu' | 'memory' | 'disk'
  current: number
  threshold: number
  timestamp: number
}

export interface ResourceSnapshot {
  cpuPercent: number
  memoryPercent: number
  memoryUsedMB: number
  memoryTotalMB: number
  loadAvg: number[]
  uptime: number
}

type AlertHandler = (alert: ResourceAlert) => void

/**
 * 客户端资源监控器
 * 定期检查 CPU/内存/磁盘，超阈值触发告警
 */
export class ResourceMonitor {
  private thresholds: Required<ResourceThresholds>
  private alertHandlers: AlertHandler[] = []
  private timer: ReturnType<typeof setInterval> | null = null
  private lastCpuInfo: { idle: number; total: number } | null = null

  constructor(thresholds?: ResourceThresholds) {
    this.thresholds = {
      cpuPercent: thresholds?.cpuPercent ?? 90,
      memoryPercent: thresholds?.memoryPercent ?? 90,
      diskPercent: thresholds?.diskPercent ?? 90,
    }
  }

  /** 注册告警回调 */
  onAlert(handler: AlertHandler): () => void {
    this.alertHandlers.push(handler)
    return () => {
      const idx = this.alertHandlers.indexOf(handler)
      if (idx >= 0) this.alertHandlers.splice(idx, 1)
    }
  }

  /** 开始监控 */
  start(interval: number = 10000): void {
    this.stop()
    this.timer = setInterval(() => this.check(), interval)
  }

  /** 停止监控 */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /** 手动执行一次检查 */
  check(): ResourceAlert[] {
    const alerts: ResourceAlert[] = []
    const snapshot = this.getSnapshot()

    if (snapshot.cpuPercent > this.thresholds.cpuPercent) {
      alerts.push({ type: 'cpu', current: snapshot.cpuPercent, threshold: this.thresholds.cpuPercent, timestamp: Date.now() })
    }
    if (snapshot.memoryPercent > this.thresholds.memoryPercent) {
      alerts.push({ type: 'memory', current: snapshot.memoryPercent, threshold: this.thresholds.memoryPercent, timestamp: Date.now() })
    }

    for (const alert of alerts) {
      for (const handler of this.alertHandlers) handler(alert)
    }
    return alerts
  }

  /** 获取当前资源快照 */
  getSnapshot(): ResourceSnapshot {
    const totalMem = os.totalmem()
    const freeMem = os.freemem()
    const usedMem = totalMem - freeMem
    const memoryPercent = Math.round((usedMem / totalMem) * 100)

    const cpuPercent = this.calculateCpuPercent()

    return {
      cpuPercent,
      memoryPercent,
      memoryUsedMB: Math.round(usedMem / 1024 / 1024),
      memoryTotalMB: Math.round(totalMem / 1024 / 1024),
      loadAvg: os.loadavg(),
      uptime: os.uptime(),
    }
  }

  /** 更新阈值 */
  setThresholds(thresholds: Partial<ResourceThresholds>): void {
    if (thresholds.cpuPercent !== undefined) this.thresholds.cpuPercent = thresholds.cpuPercent
    if (thresholds.memoryPercent !== undefined) this.thresholds.memoryPercent = thresholds.memoryPercent
    if (thresholds.diskPercent !== undefined) this.thresholds.diskPercent = thresholds.diskPercent
  }

  private calculateCpuPercent(): number {
    const cpus = os.cpus()
    let idle = 0
    let total = 0
    for (const cpu of cpus) {
      idle += cpu.times.idle
      total += cpu.times.user + cpu.times.nice + cpu.times.sys + cpu.times.idle + cpu.times.irq
    }

    if (this.lastCpuInfo) {
      const idleDiff = idle - this.lastCpuInfo.idle
      const totalDiff = total - this.lastCpuInfo.total
      this.lastCpuInfo = { idle, total }
      if (totalDiff === 0) return 0
      return Math.round((1 - idleDiff / totalDiff) * 100)
    }

    this.lastCpuInfo = { idle, total }
    const loadAvg = os.loadavg()[0]
    return Math.min(100, Math.round((loadAvg / cpus.length) * 100))
  }
}
